import React from 'react';
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    PhotoCamera,
    ShapeLine,
    AutoFixHigh,
    Download,
    ShoppingCart,
    Palette,
    ExpandMore,
    OpenWith
} from '@mui/icons-material'
import TopNav from "./TopNav.jsx";
import Footer from "./Footer.jsx";
import ImageUpload from "./ImageUpload.jsx";
import ColorPickerControl from "./ColorPickerControl.jsx";
import PhotoFramingControl from "./PhotoFramingControl.jsx";
import GlassCard from "./GlassCard.jsx";
import ProcessingStatus from "./ProcessingStatus.jsx";
import TravelMapSection from "./TravelMapSection.jsx";
import WorldMapSection from "./WorldMapSection.jsx";
import EuropeMapSection from "./EuropeMapSection.jsx";
import AfricaMapSection from "./AfricaMapSection.jsx";
import AsiaMapSection from "./AsiaMapSection.jsx";
import SouthAmericaMapSection from "./SouthAmericaMapSection.jsx";
import LatinAmericaMapSection from "./LatinAmericaMapSection.jsx";
import EUMapSection from "./EUMapSection.jsx";
import NationalParksMapSection from "./NationalParksMapSection.jsx";
import CanadaMapSection from "./CanadaMapSection.jsx";
import UKCountiesMapSection from "./UKCountiesMapSection.jsx";
import AustraliaMapSection from "./AustraliaMapSection.jsx";
import NewZealandMapSection from "./NewZealandMapSection.jsx";
import MexicoMapSection from "./MexicoMapSection.jsx";
import GermanyMapSection from "./GermanyMapSection.jsx";
import FranceMapSection from "./FranceMapSection.jsx";
import ItalyMapSection from "./ItalyMapSection.jsx";
import SpainMapSection from "./SpainMapSection.jsx";
import SwitzerlandMapSection from "./SwitzerlandMapSection.jsx";
import JapanMapSection from "./JapanMapSection.jsx";
import IndiaMapSection from "./IndiaMapSection.jsx";
import BrazilMapSection from "./BrazilMapSection.jsx";
import ArgentinaMapSection from "./ArgentinaMapSection.jsx";
import CaribbeanMapSection from "./CaribbeanMapSection.jsx";
import QuickOrderStrip from "./QuickOrderStrip.jsx";
import SelectDropdown from "./SelectDropdown.jsx";
import StepProgress from "./StepProgress.jsx";
import ActionButton from "./ActionButton.jsx";
import ShareControl from "./ShareControl.jsx";
import SaveMapButton from "./SaveMapButton.jsx";
import TravelSaveContext from "./travel/TravelSaveContext";
import { useAuth } from "../auth/AuthContext";
import InlineStatus from "./InlineStatus.jsx";
import EmptyState from "./EmptyState.jsx";
import SearchablePicker from "./SearchablePicker.jsx";
import CategoryChips from "./CategoryChips.jsx";
import { bindActionCreators } from "redux";
import * as shapeActions from "../actions/shapeActions"
import { shapesStates } from "../shapesStates"
import { shapesCountries } from "../shapesCountries"
import { shapesContinents } from "../shapesContinents"
import { shapesIslands } from "../shapesIslands"
import { shapesParks } from "../shapesParks"
import { shapesLakes } from "../shapesLakes"
import { categories } from "../categories"
import { getCountryFlag } from "../countryFlags"
import { orderProduct } from "../actions/shapeActions";
import { storage } from "../firebaseApp";
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getMapForReopen } from "../utils/mapsRepository";
import { makeThumbnail } from "../utils/mapThumbnail";
import { dataUrlToBlob } from "../utils/blob";
import {
    trackImageUpload,
    trackShapeSelection,
    trackDownload,
    trackColorChange,
    trackError,
    trackPageView,
    trackCategorySelection,
    trackBorderSizeChange,
    trackShapingStart,
    trackShapingComplete,
    trackMapTypeChange,
    trackAddToCart,
} from "../utils/analytics";
import {
    trackFbPageView,
    trackFbImageUpload,
    trackFbAddToCart,
    trackFbDownload,
} from "../utils/facebookPixel";
import {
    trackPinPageView,
    trackPinImageUpload,
    trackPinAddToCart,
    trackPinDownload,
} from "../utils/pinterestTag";
import { PALETTE } from "../theme";

const SHAPES_BY_CATEGORY = {
    'USA States': shapesStates,
    'Countries': shapesCountries,
    'Continents': shapesContinents,
    'Islands': shapesIslands,
    'National Parks': shapesParks,
    'Lakes': shapesLakes
};

const ALL_SHAPES = [
    ...shapesStates,
    ...shapesCountries,
    ...shapesContinents,
    ...shapesIslands,
    ...shapesParks,
    ...shapesLakes
];

const CATEGORY_SLUGS = {
    'USA States': 'states',
    'Countries': 'countries',
    'Continents': 'continents',
    'Islands': 'islands',
    'National Parks': 'parks',
    'Lakes': 'lakes'
};
const CATEGORY_FROM_SLUG = Object.fromEntries(
    Object.entries(CATEGORY_SLUGS).map(([k, v]) => [v, k])
);

const CATEGORY_ICONS = {
    'USA States': '🇺🇸',
    'Countries': '🌐',
    'Continents': '🌎',
    'Islands': '🏝️',
    'National Parks': '🏞️',
    'Lakes': '💧'
};

// Icon to show on each region-group subheader in the shape dropdown.
// Per-shape items use their own `icon` field (flags, themed emojis).
const GROUP_HEADER_ICONS = {
    // Islands
    'Mediterranean': '🏛️',
    'North Atlantic': '🌊',
    'Caribbean': '🌴',
    'Hawaii': '🌺',
    'Asia & Pacific': '🌏',
    'Indian Ocean & Oceania': '🦘',
    'New York': '🗽',
    // Lakes
    'Great Lakes': '🦬',
    'North America': '🌎',
    'Europe': '🇪🇺',
    'Asia': '🐉',
    'Africa': '🦁',
    'South America': '🦙',
    // National Parks
    'Rocky Mountain': '⛰️',
    'Southwest Canyons': '🏜️',
    'California': '🌲',
    'Pacific Northwest': '🏔️',
    'Eastern US': '🍁',
    'Texas & Alaska': '🤠',
};

const MAP_TYPE_GROUPS = [
    { label: '🇺🇸 United States', items: [
        { value: 'usa',   text: '🇺🇸 USA States' },
        { value: 'parks', text: '🦬 US National Parks' }
    ]},
    { label: '🇪🇺 Europe', items: [
        { value: 'europe',      text: '🏰 All Europe (Countries)' },
        { value: 'eu',          text: '🇪🇺 European Union (Countries)' },
        { value: 'uk-counties', text: '🇬🇧 UK Counties' },
        { value: 'germany',     text: '🇩🇪 Germany (Länder)' },
        { value: 'france',      text: '🇫🇷 France (Départements)' },
        { value: 'italy',       text: '🇮🇹 Italy (Regions)' },
        { value: 'spain',       text: '🇪🇸 Spain (Provinces)' },
        { value: 'switzerland', text: '🇨🇭 Switzerland (Cantons)' }
    ]},
    { label: '🇨🇦 Canada', items: [
        { value: 'canada', text: '🦫 Provinces & Territories' }
    ]},
    { label: '🌎 Americas', items: [
        { value: 'mexico',         text: '🇲🇽 Mexico (States)' },
        { value: 'south-america',  text: '🦙 South America (Countries)' },
        { value: 'latin-america',  text: '🌮 Latin America (Countries)' },
        { value: 'brazil',         text: '🇧🇷 Brazil (States)' },
        { value: 'argentina',      text: '🇦🇷 Argentina (Provinces)' },
        { value: 'caribbean',      text: '🏝️ Caribbean (Islands)' }
    ]},
    { label: '🌏 Asia & Oceania', items: [
        { value: 'asia',         text: '🐉 Asia (Countries)' },
        { value: 'japan',        text: '🇯🇵 Japan (Prefectures)' },
        { value: 'india',        text: '🇮🇳 India (States)' },
        { value: 'australia',    text: '🇦🇺 Australia (States)' },
        { value: 'new-zealand',  text: '🇳🇿 New Zealand (Regions)' }
    ]},
    { label: '🌍 Africa', items: [
        { value: 'africa', text: '🦒 Africa (Countries)' }
    ]},
    { label: '🌐 Global', items: [
        { value: 'world', text: '🗺️ World Map (Countries)' }
    ]}
];

// Flatten MAP_TYPE_GROUPS → { mapType: cleanLabel } for the saved-map breadcrumb
// (e.g. 'usa' → 'USA States'). Strips the leading flag/emoji from the option text.
const MAP_TYPE_LABELS = {};
MAP_TYPE_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
        MAP_TYPE_LABELS[item.value] = item.text.replace(/^[^\w(]+/, '').trim();
    });
});

class Main extends React.Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();


        // Read initial values from URL
        const urlParams = new URLSearchParams(window.location.search);
        const mapParam = urlParams.get('map');
        const categoryParam = urlParams.get('category');
        const shapeParam = urlParams.get('shape');

        // `tool` is the route-level signal: 'travel' for /travel-maps,
        // 'shaper' for /photo-shaper. activeTab is kept around (0=shaper,
        // 1=travel) because lots of methods key off it — but it's no longer
        // user-controllable; each page is single-tool now.
        const initialTab = props.tool === 'travel' ? 1 : 0;
        const validMapTypes = [
            'usa', 'parks', 'canada', 'europe', 'eu', 'asia', 'africa',
            'south-america', 'latin-america', 'world',
            'uk-counties', 'australia', 'new-zealand', 'mexico',
            'germany', 'france', 'italy', 'spain', 'switzerland',
            'japan', 'india', 'brazil', 'argentina', 'caribbean'
        ];
        const initialMapType = validMapTypes.includes(mapParam) ? mapParam : 'usa';

        let initialCategory = CATEGORY_FROM_SLUG[categoryParam] || undefined;
        let initialShape = undefined;
        if (shapeParam) {
            const match = ALL_SHAPES.find(s => s.key === shapeParam);
            if (match) {
                initialShape = shapeParam;
                if (!initialCategory) {
                    initialCategory = match.category === 'US States' ? 'USA States' : match.category;
                }
            }
        }

        this.state = {
        activeTab: initialTab,
        mapType: initialMapType,
        selectedState: undefined,
        selectedCategory: initialCategory,
        selectedShape: initialShape,
        selectedImageUri: undefined,
        selectedBorder: 0,
        loading: false,
        progress: 0,
        borderColor: {
            r: 0,
            g: 0,
            b: 0,
            a: 255,
        },
        framing: { offsetX: 0, offsetY: 0, zoom: 1 },
        sourceDims: null, // {width, height} of the uploaded photo
        shapeDims: null,  // {width, height} of the selected shape PNG
        // The saved map currently open (from My Maps via ?open=<id>), or null
        // for a fresh creation. Drives Save → overwrite-vs-new.
        openMap: null,
        // Travel reopen bridge (see TravelSaveContext): the pending restore for
        // the travel section, and the open-map identity for its Save button.
        travelReopen: null,
        travelOpenMap: null,
        // Inline status (replaces bottom-corner toasts). `message` is the line
        // shown to the user; `variant` is success/error/info; cleared by the
        // InlineStatus component itself after autoHide.
        inlineStatus: { message: '', variant: 'info' }
    };
    }

    // Keep the tab title in sync imperatively. <Helmet> alone is unreliable on
    // the initial client render in this CRA + React 18 app (see useDocumentTitle),
    // so we set document.title directly whenever the page identity changes.
    syncTitle = () => {
        const meta = this.getPageMeta();
        if (meta && meta.title && document.title !== meta.title) {
            document.title = meta.title;
        }
    };

    componentDidMount() {
        // Ensure selectedBorder is always a valid number
        if (typeof this.state.selectedBorder !== 'number') {
            this.setState({ selectedBorder: 0 });
        }
        this.syncTitle();
        trackPageView(window.location.pathname + window.location.search);
        trackFbPageView();
        trackPinPageView();
        // Initial-state dims: if the page boots with a shape pre-selected via
        // URL params, componentDidUpdate won't fire — load dims explicitly.
        if (this.state.selectedShape) {
            const shape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
            if (shape) this.loadDims(shape.path, 'shapeDims');
        }
        if (this.state.selectedImageUri) {
            this.loadDims(this.state.selectedImageUri, 'sourceDims');
        }
        // Reopen a saved map (My Maps → Reopen sends ?open=<id>). Gated on auth
        // being ready so a cold load / refresh of the URL works too (see below).
        this.maybeReopen();
    }

    // Trigger a saved-map reopen once Firebase has restored the session. Called
    // from both mount and update so it fires whether auth is already live (in-app
    // navigation) or restores a moment later (cold page load / refresh).
    maybeReopen = () => {
        if (this.reopenAttempted) return;
        const openId = new URLSearchParams(window.location.search).get('open');
        if (!openId) return;
        if (!this.props.authReady || !this.props.isSignedIn) return;
        this.reopenAttempted = true;
        if (this.state.activeTab === 0) {
            this.loadSavedMap(openId);
        } else if (this.state.activeTab === 1) {
            this.loadSavedTravelMap(openId);
        }
    };

    // Reopen a saved travel map: switch to its region (so the right section
    // mounts) and hand the visited/colour/stats down via TravelSaveContext.
    loadSavedTravelMap = async (id) => {
        try {
            this.showToast('Loading your saved map…', 'info');
            const rec = await getMapForReopen(id);
            if (rec.type !== 'travel') return;
            const s = rec.settings || {};
            this.setState({
                mapType: s.mapType || this.state.mapType,
                travelReopen: { id: rec.id, name: rec.name, settings: s },
                travelOpenMap: { id: rec.id, name: rec.name },
            });
            this.clearStatus();
        } catch (err) {
            this.showToast(err.message || 'Could not load that saved map.', 'error');
        }
    };

    // ── Saved-map adapter (shaper) ──────────────────────────────────────────
    // The only shaper-specific save code: produce a restorable snapshot, and
    // apply one on reopen. Everything else (auth, Storage, Firestore, the Save
    // dialog, My Maps) is shared.

    getShaperSnapshot = async () => {
        const shape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
        const shapeName = shape?.name || 'Map';
        const source = this.state.selectedImageUri
            ? dataUrlToBlob(this.state.selectedImageUri)
            : null;
        const thumbnailBlob = this.state.shapedPhoto?.data
            ? await makeThumbnail(this.state.shapedPhoto.data)
            : null;
        return {
            type: 'shaper',
            defaultName: shapeName,
            breadcrumb: ['Shaped', this.state.selectedCategory, shapeName].filter(Boolean),
            settings: {
                selectedCategory: this.state.selectedCategory,
                selectedShape: this.state.selectedShape,
                selectedBorder: this.state.selectedBorder,
                borderColor: this.state.borderColor,
                framing: this.state.framing,
            },
            assets: { source },
            thumbnailBlob,
        };
    };

    loadSavedMap = async (id) => {
        try {
            this.showToast('Loading your saved map…', 'info');
            const rec = await getMapForReopen(id);
            if (rec.type !== 'shaper') return; // wrong tool for this route
            const s = rec.settings || {};
            this.setState(
                {
                    selectedCategory: s.selectedCategory,
                    selectedShape: s.selectedShape,
                    selectedBorder: typeof s.selectedBorder === 'number' ? s.selectedBorder : 0,
                    borderColor: s.borderColor || this.state.borderColor,
                    framing: s.framing || { offsetX: 0, offsetY: 0, zoom: 1 },
                    selectedImageUri: rec.sourceDataUrl || undefined,
                    shapedPhoto: undefined,
                    openMap: { id: rec.id, name: rec.name },
                },
                () => {
                    // Regenerate the shaped result from the restored photo + settings.
                    if (this.state.selectedImageUri && this.state.selectedShape) {
                        this.shapePhoto();
                    } else {
                        this.clearStatus();
                    }
                }
            );
        } catch (err) {
            this.showToast(err.message || 'Could not load that saved map.', 'error');
        }
    };

    componentDidUpdate(prevProps, prevState) {
        // Title depends on tool / map / selected shape — refresh it on change.
        if (
            this.state.activeTab !== prevState.activeTab ||
            this.state.mapType !== prevState.mapType ||
            this.state.selectedShape !== prevState.selectedShape
        ) {
            this.syncTitle();
        }
        // Whenever the photo or shape selection changes, reload dimensions so
        // we know how much pan slack each axis has (for disabling arrows).
        if (this.state.selectedImageUri !== prevState.selectedImageUri) {
            this.loadDims(this.state.selectedImageUri, 'sourceDims');
        }
        if (this.state.selectedShape !== prevState.selectedShape) {
            const shape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
            this.loadDims(shape ? shape.path : undefined, 'shapeDims');
        }
        // Auth may have become ready after a cold load — retry the reopen.
        if (
            this.props.authReady !== prevProps.authReady ||
            this.props.isSignedIn !== prevProps.isSignedIn
        ) {
            this.maybeReopen();
        }
    }

    loadDims = async (uri, stateKey) => {
        if (!uri) {
            this.setState({ [stateKey]: null });
            return;
        }
        const requestUri = uri;
        const isStillCurrent = () => {
            const current = stateKey === 'sourceDims' ? this.state.selectedImageUri : (() => {
                const s = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
                return s ? s.path : undefined;
            })();
            return current === requestUri;
        };

        // Phone photos often carry an EXIF "rotation" tag — they're stored as
        // portrait bytes but display as landscape (or vice versa). createImage-
        // Bitmap with imageOrientation:'from-image' returns the *visible*
        // dimensions, which is what the shapener also operates on (drawImage
        // honors EXIF in modern browsers), so the two stay in sync.
        try {
            const resp = await fetch(requestUri);
            const blob = await resp.blob();
            const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
            if (!isStillCurrent()) { bitmap.close && bitmap.close(); return; }
            this.setState({ [stateKey]: { width: bitmap.width, height: bitmap.height } });
            bitmap.close && bitmap.close();
            return;
        } catch (_) {
            // Fall through to Image() fallback (older browsers / fetch errors).
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (!isStillCurrent()) return;
            this.setState({ [stateKey]: { width: img.naturalWidth, height: img.naturalHeight } });
        };
        img.onerror = () => this.setState({ [stateKey]: null });
        img.src = requestUri;
    }

    getFramingSlack = () => {
        const { sourceDims, shapeDims, framing } = this.state;
        if (!sourceDims || !shapeDims) {
            // Without dims yet, assume both axes pan-able — UI never spuriously
            // disables before the user has selected both inputs.
            return { slackX: 1, slackY: 1 };
        }
        const shapeRatio = shapeDims.height / shapeDims.width;
        const sourceRatio = sourceDims.height / sourceDims.width;
        let baseCropW, baseCropH;
        if (sourceRatio > shapeRatio) {
            baseCropW = sourceDims.width;
            baseCropH = Math.round(sourceDims.width * shapeRatio);
        } else {
            baseCropH = sourceDims.height;
            baseCropW = Math.round(sourceDims.height / shapeRatio);
        }
        const zoom = Math.max(1, framing.zoom || 1);
        const cropW = Math.max(1, Math.min(sourceDims.width, Math.round(baseCropW / zoom)));
        const cropH = Math.max(1, Math.min(sourceDims.height, Math.round(baseCropH / zoom)));
        return {
            slackX: sourceDims.width - cropW,
            slackY: sourceDims.height - cropH
        };
    }

    onImageSelected = (image) => {
        this.setState({
            selectedImageUri: image.uri,
            shapedPhoto: undefined, // Clear any previous shaped result
            framing: { offsetX: 0, offsetY: 0, zoom: 1 } // new photo, reset framing
        });
        this.showToast('Photo uploaded successfully!', 'success');
        // Track image upload
        trackImageUpload(image.name || 'unknown');
        trackFbImageUpload();
        trackPinImageUpload();
    };

    handleFramingChange = (framing) => {
        this.setState({ framing });
    };

    showToast = (message, type) => {
        // Adapter for existing call sites: route status through the new
        // InlineStatus component. The component owns its own auto-dismiss,
        // so we don't need the old setTimeout dance.
        const variant = type === 'success' ? 'success' : type === 'error' ? 'error' : 'info';
        this.setState({ inlineStatus: { message, variant } });
    };

    clearStatus = () => {
        this.setState({ inlineStatus: { message: '', variant: 'info' } });
    };

    dataURLtoBlob = (dataurl) => {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    };

    saveToFile = async () => {
        try {
            // Export as PNG to preserve the alpha channel — the area outside
            // the shape silhouette stays transparent, so the file prints
            // cleanly on dark cups / t-shirts / hoodies, no white halo.
            const dataUrl = this.canvasRef.current.toDataURL("image/png");
            const blob = this.dataURLtoBlob(dataUrl);

            // Generate meaningful filename based on shape
            const shape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
            const shapeName = shape ? shape.name.toLowerCase().replace(/ /g, '-') : 'map-shaped';
            const filename = `map-shaped-${shapeName}.png`;

            // Use File System Access API to let user choose save location (modern browsers)
            if ('showSaveFilePicker' in window) {
                try {
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: filename,
                        types: [{
                            description: 'PNG Image',
                            accept: { 'image/png': ['.png'] }
                        }]
                    });
                    
                    const writable = await fileHandle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                    
                    this.showToast('Photo downloaded successfully!', 'success');
                    trackDownload({
                        kind: 'shaper',
                        shape: shape?.name || 'unknown',
                        category: this.state.selectedCategory,
                        borderSize: this.state.selectedBorder,
                    });
                    trackFbDownload(`Map Shaper - ${shape?.name || 'unknown'}`);
                    trackPinDownload(`Map Shaper - ${shape?.name || 'unknown'}`);
                    return;
                } catch (err) {
                    // User cancelled or error - fall back to download
                    if (err.name !== 'AbortError') {
                        console.warn('File System Access API failed, using fallback:', err);
                    }
                }
            }

            // Fallback for browsers without File System Access API
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
            this.showToast('Photo downloaded successfully!', 'success');
            trackDownload({
                kind: 'shaper',
                shape: shape?.name || 'unknown',
                category: this.state.selectedCategory,
                borderSize: this.state.selectedBorder,
            });
            trackFbDownload(`Map Shaper - ${shape?.name || 'unknown'}`);
            trackPinDownload(`Map Shaper - ${shape?.name || 'unknown'}`);
        } catch (error) {
            this.showToast('Error downloading photo. Please try again.', 'error');
            trackError('save_error', error.message);
        }
    };

    getShareBlob = async () => {
        const dataUrl = this.canvasRef.current.toDataURL("image/png");
        return this.dataURLtoBlob(dataUrl);
    };

    /**
     * Upload the current shaped canvas to Firebase Storage and resolve with
     * the public download URL. Called by ProductGallery when the user clicks
     * Order on any product card.
     */
    prepareShaperUpload = (onProgress) => {
        return new Promise((resolve, reject) => {
            this.canvasRef.current.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Failed to capture shaped image.'));
                    return;
                }
                const uniqueId =
                    Math.random().toString(36).substring(2) +
                    Date.now().toString(36) +
                    '.png';
                const storageRef = ref(storage, 'images/' + uniqueId);
                const uploadTask = uploadBytesResumable(storageRef, blob);

                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const pct = Math.round(
                            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        );
                        if (onProgress) onProgress(pct);
                    },
                    (error) => {
                        console.error('Upload error:', error);
                        trackError('cup_upload_error', error.message);
                        reject(error);
                    },
                    async () => {
                        try {
                            const url = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(url);
                        } catch (err) {
                            reject(err);
                        }
                    }
                );
            }, 'image/png');
        });
    };

    handleShaperProductOrder = ({ product, label, imageUrl }) => {
        const shape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
        const shapeName = shape?.name || 'unknown';
        const source = `Map Shaper - ${shapeName}`;
        trackAddToCart({
            product: product.analyticsKey,
            kind: 'shaper',
            shape: shapeName,
        });
        trackFbAddToCart(product.name, source);
        trackPinAddToCart(product.name, source);
        this.props.orderProduct(imageUrl, product, label);
    };

    onCategoryChange = (e) => {
        this.setState({ selectedCategory: e.target.value, selectedShape: undefined });
        this.updateURL(this.state.activeTab, this.state.mapType, e.target.value, undefined);
        trackCategorySelection(e.target.value);
    };

    onShapeChange = (e) => {
        const shapeKey = e.target.value;
        this.setState({ selectedShape: shapeKey });
        this.updateURL(this.state.activeTab, this.state.mapType, this.state.selectedCategory, shapeKey);
        const shapeName = ALL_SHAPES.find(sh => sh.key === shapeKey)?.name || 'unknown';
        trackShapeSelection(shapeName, this.state.selectedCategory);
    };

    onBorderChange = (e) => {
        console.log('Border change:', e.target.value, typeof e.target.value);
        // Ensure we always set a valid numeric value
        const value = parseInt(e.target.value) || 0;
        this.setState({ selectedBorder: value });
        trackBorderSizeChange(value);
    };

    handleColorPickerChange = (color) => {
        this.setState({ borderColor: {
            r: typeof color.rgb.r === 'number' ? color.rgb.r : parseInt(color.rgb.r),
            g: typeof color.rgb.g === 'number' ? color.rgb.g : parseInt(color.rgb.g),
            b: typeof color.rgb.b === 'number' ? color.rgb.b : parseInt(color.rgb.b),
            a: typeof color.rgb.a === 'number' ? Math.round(color.rgb.a * 255) : parseInt(color.rgb.a)
        }});
    };

    handleColorSliderChange = (channel, value) => {
        this.setState({
            borderColor: {
                ...this.state.borderColor,
                [channel]: value
            }
        });
        // Track color change
        trackColorChange(`${channel}:${value}`);
    };

    handlePresetColorClick = (color) => {
        this.setState({ borderColor: color });
        // Track preset color selection
        trackColorChange(`preset:${color.r},${color.g},${color.b}`);
    };

    shapePhoto = () => {
        try {
        const path = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape).path;
        const shapeName = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape)?.name || 'unknown';
        
        this.setState({ shapedPhoto: undefined });
        trackShapingStart(shapeName, this.state.selectedCategory, this.state.selectedBorder);

        this.props.shapeActions.shape(
            this.state.selectedImageUri,
            path,
                this.canvasRef.current,
                {
                    border: { width: this.state.selectedBorder, color: this.state.borderColor },
                    framing: this.state.framing
                },
                fileData => {
                    this.setState({ shapedPhoto: fileData });
                    this.showToast('Photo shaped successfully!', 'success');
                    trackShapingComplete(shapeName, this.state.selectedCategory, this.state.selectedBorder);
                }
            );
        } catch (error) {
            this.showToast('Error shaping photo. Please try again.', 'error');
            trackError('shape_error', error.message);
        }
    };

    updateURL = (tab, mapType, category, shape) => {
        const { setSearchParams } = this.props;
        if (!setSearchParams) return;
        const params = {};
        if (tab === 1) {
            // Travel maps: only the sub-map selection lives in the query.
            // The route itself (/travel-maps) already encodes the tool.
            if (mapType) params.map = mapType;
        } else if (category) {
            const categorySlug = CATEGORY_SLUGS[category] || 'countries';
            params.category = categorySlug;
            if (shape) params.shape = shape;
        }
        setSearchParams(params, { replace: true });
    };

    handleMapTypeChange = (newMapType) => {
        // Switching region is a different map — drop any open saved-map link so
        // Save creates a new map instead of offering to overwrite the previous
        // region's saved map.
        this.setState({ mapType: newMapType, travelOpenMap: null, travelReopen: null });
        this.updateURL(this.state.activeTab, newMapType, undefined, undefined);
        trackMapTypeChange(newMapType);
    };

    getCurrentStep = () => {
        if (!this.state.selectedImageUri) return 0;
        if (!this.state.selectedShape) return 1;
        if (!this.state.shapedPhoto) return 2;
        return 3;
    };

    getPageMeta = () => {
        const { activeTab, mapType, selectedShape } = this.state;

        if (activeTab === 0) {
            if (selectedShape) {
                const shape = ALL_SHAPES.find(sh => sh.key === selectedShape);
                if (shape) {
                    return {
                        title: `${shape.name} Photo Shaper - Create ${shape.name} Shaped Photos | Map Shaper`,
                        description: `Transform your photos into a ${shape.name} silhouette. Upload any photo and shape it into ${shape.name}. Add custom borders and colors. Order cups and t-shirts.`
                    };
                }
            }
            return {
                title: 'Map Shaper - Create Photos in State & Country Silhouettes | USA States & Countries',
                description: 'Transform your photos into stunning state and country silhouettes. Choose from all 50 USA states and 100+ countries. Add custom borders and colors. Free online tool.'
            };
        }
        
        const mapMeta = {
            'usa': {
                title: 'US States Travel Map - Track Visited States | Map Shaper',
                description: 'Track which US states you\'ve visited with our interactive map. Mark your travels, download your map, or order custom mugs and t-shirts.'
            },
            'parks': {
                title: 'US National Parks Tracker - All 63 Parks | Map Shaper',
                description: 'Track all 63 US National Parks you\'ve visited. Interactive map with park locations in Alaska, Hawaii, and US territories. Download or order custom products.'
            },
            'world': {
                title: 'World Travel Map - Track Visited Countries | Map Shaper',
                description: 'Mark countries you\'ve visited on our interactive world map. Track your global travels and order custom mugs or t-shirts with your personalized map.'
            },
            'europe': {
                title: 'Europe Travel Map - Track Visited European Countries | Map Shaper',
                description: 'Track your travels across Europe with our interactive map. Mark visited countries from Portugal to Russia. Download or order custom products.'
            },
            'eu': {
                title: 'European Union Map - Track EU Countries Visited | Map Shaper',
                description: 'Track which EU member countries you\'ve visited. Interactive map of all 27 European Union nations. Order custom mugs and t-shirts.'
            },
            'asia': {
                title: 'Asia Travel Map - Track Visited Asian Countries | Map Shaper',
                description: 'Mark Asian countries you\'ve visited on our interactive map. From Japan to India, track your Asian adventures. Order custom products.'
            },
            'africa': {
                title: 'Africa Travel Map - Track Visited African Countries | Map Shaper',
                description: 'Track your travels across Africa with our interactive continent map. Mark visited countries and order custom mugs or t-shirts.'
            },
            'south-america': {
                title: 'South America Travel Map - Track Visited Countries | Map Shaper',
                description: 'Mark South American countries you\'ve visited. Interactive map from Brazil to Argentina. Download or order custom products.'
            },
            'latin-america': {
                title: 'Latin America Travel Map - Track Visited Countries | Map Shaper',
                description: 'Track travels across Latin America including Mexico, Central America, South America, and the Caribbean. Order custom products.'
            },
            'canada': {
                title: 'Canada Travel Map - Track Visited Provinces & Territories | Map Shaper',
                description: 'Track which Canadian provinces and territories you\'ve visited. Interactive map of all 13 regions. Download or order custom cups and t-shirts.'
            },
            'uk-counties': {
                title: 'UK Counties Travel Map - Track Visited Counties | Map Shaper',
                description: 'Track which UK counties and unitary authorities you\'ve visited across England, Scotland, Wales, and Northern Ireland. Order custom cups and t-shirts.'
            },
            'australia': {
                title: 'Australia Travel Map - Track Visited States & Territories | Map Shaper',
                description: 'Track which Australian states and territories you\'ve visited. Interactive map of all 8 regions. Download or order custom cups and t-shirts.'
            },
            'new-zealand': {
                title: 'New Zealand Travel Map - Track Visited Regions | Map Shaper',
                description: 'Track which New Zealand regions you\'ve visited. Interactive map of all 16 regions plus the Chatham Islands. Order custom cups and t-shirts.'
            },
            'mexico': {
                title: 'Mexico Travel Map - Track Visited States | Map Shaper',
                description: 'Track which Mexican states you\'ve visited. Interactive map of all 32 federal entities. Download or order custom cups and t-shirts.'
            },
            'germany': {
                title: 'Germany Travel Map - Track Visited Länder | Map Shaper',
                description: 'Track which German Länder (federal states) you\'ve visited. Interactive map of all 16 states. Order custom cups and t-shirts.'
            },
            'france': {
                title: 'France Travel Map - Track Visited Départements | Map Shaper',
                description: 'Track which French départements you\'ve visited. Interactive map of all 96 metropolitan départements. Order custom cups and t-shirts.'
            },
            'italy': {
                title: 'Italy Travel Map - Track Visited Regions | Map Shaper',
                description: 'Track which Italian regions you\'ve visited. Interactive map of all 20 regions. Order custom cups and t-shirts.'
            },
            'spain': {
                title: 'Spain Travel Map - Track Visited Provinces | Map Shaper',
                description: 'Track which Spanish provinces you\'ve visited. Interactive map of all 50 provinces. Order custom cups and t-shirts.'
            },
            'switzerland': {
                title: 'Switzerland Travel Map - Track Visited Cantons | Map Shaper',
                description: 'Track which Swiss cantons you\'ve visited. Interactive map of all 26 cantons. Order custom cups and t-shirts.'
            },
            'japan': {
                title: 'Japan Travel Map - Track Visited Prefectures | Map Shaper',
                description: 'Track which Japanese prefectures you\'ve visited. Interactive map of all 47 prefectures. Order custom cups and t-shirts.'
            },
            'india': {
                title: 'India Travel Map - Track Visited States | Map Shaper',
                description: 'Track which Indian states and union territories you\'ve visited. Interactive map of all 36 regions. Order custom cups and t-shirts.'
            },
            'brazil': {
                title: 'Brazil Travel Map - Track Visited States | Map Shaper',
                description: 'Track which Brazilian states you\'ve visited. Interactive map of all 26 states plus the Federal District. Order custom cups and t-shirts.'
            },
            'argentina': {
                title: 'Argentina Travel Map - Track Visited Provinces | Map Shaper',
                description: 'Track which Argentine provinces you\'ve visited. Interactive map of all 23 provinces plus CABA. Order custom cups and t-shirts.'
            },
            'caribbean': {
                title: 'Caribbean Travel Map - Track Visited Islands | Map Shaper',
                description: 'Track which Caribbean islands you\'ve visited across 26 island nations and territories. Download or order custom cups and t-shirts.'
            }
        };
        
        return mapMeta[mapType] || mapMeta['usa'];
    };

    getSteps = () => {
        return [
            { label: 'Select Photo', icon: <PhotoCamera /> },
            { label: 'Choose Map Shape', icon: <ShapeLine /> },
            { label: 'Shape Photo', icon: <AutoFixHigh /> },
            { label: 'Order a Product', icon: <ShoppingCart /> }
        ];
    };

    render() {
        const categoryIcon = (category) => CATEGORY_ICONS[category] || '🌐';

        const categoryOptions = categories.map(category => ({
            key: category,
            text: `${categoryIcon(category)} ${category}`,
            value: category,
        }));

        const buildShapeOption = (shape, fallbackIcon) => {
            const icon = shape.icon || fallbackIcon;
            const label = shape.description ? `${shape.name} (${shape.description})` : shape.name;
            return {
                key: shape.key,
                text: `${icon} ${label}`.trim(),
                value: shape.key,
            };
        };

        const buildShapeOptions = (category) => {
            const list = SHAPES_BY_CATEGORY[category] || [];
            const isCountries = category === 'Countries';
            const fallbackIcon = CATEGORY_ICONS[category] || '🌐';
            // Countries keep the existing flag-emoji-per-shape rendering.
            if (isCountries) {
                return {
                    options: list.map(shape => ({
                        key: shape.key,
                        text: `${getCountryFlag(shape.key)} ${shape.description ? `${shape.name} (${shape.description})` : shape.name}`.trim(),
                        value: shape.key,
                    })),
                };
            }
            // If shapes carry a `group` field, render a grouped dropdown.
            const hasGroups = list.some(s => s.group);
            if (!hasGroups) {
                return { options: list.map(s => buildShapeOption(s, fallbackIcon)) };
            }
            // Note: don't use `new Map()` here — the MUI icon `Map` is imported
            // above and webpack tree-shaking will mistakenly bind the local
            // `Map` reference to the icon. Group with a plain object keyed by
            // group name, preserving insertion order.
            const groupOrder = [];
            const byGroup = {};
            for (const shape of list) {
                const g = shape.group || 'Other';
                if (!byGroup[g]) {
                    byGroup[g] = [];
                    groupOrder.push(g);
                }
                byGroup[g].push(shape);
            }
            return {
                groups: groupOrder.map(g => {
                    const items = byGroup[g];
                    const groupIcon = GROUP_HEADER_ICONS[g] || fallbackIcon;
                    return {
                        label: `${groupIcon} ${g}`,
                        items: items.map(s => buildShapeOption(s, fallbackIcon)),
                    };
                }),
            };
        };

        const shapeOptionsResult = !this.state.selectedCategory ? { options: [] } : buildShapeOptions(this.state.selectedCategory);

        const borderSizeOptions = [
            { key: 0, text: 'No Border', value: 0 },
            { key: 1, text: '1px', value: 1 },
            { key: 2, text: '2px', value: 2 },
            { key: 3, text: '3px', value: 3 },
            { key: 4, text: '4px', value: 4 },
            { key: 5, text: '5px', value: 5 },
        ];

        const currentStep = this.getCurrentStep();
        const steps = this.getSteps();

        const shaperShape = ALL_SHAPES.find(sh => sh.key === this.state.selectedShape);
        const shaperShapeName = shaperShape?.name || 'map';
        const shaperFileSlug = shaperShape ? shaperShape.name.toLowerCase().replace(/ /g, '-') : 'map-shaped';

        // Bridges the shared save layer into the Travel sections (see TravelSaveContext).
        const travelSaveValue = {
            regionKey: this.state.mapType,
            regionLabel: MAP_TYPE_LABELS[this.state.mapType] || 'Travel map',
            reopen: this.state.travelReopen,
            consumeReopen: () => this.setState({ travelReopen: null }),
            openMap: this.state.travelOpenMap,
            setOpenMap: (m) => this.setState({ travelOpenMap: m }),
            onStatus: this.showToast,
        };

        return (
            <Box sx={{
                // Fallback 100vh; on browsers that support it, 100dvh tracks
                // the *visible* viewport (excludes mobile Safari's address bar)
                // so the bottom of the page is never hidden behind the chrome.
                minHeight: '100vh',
                '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
                backgroundColor: PALETTE.page,
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden'
            }}>

                <TopNav />

                <Box component="main" sx={{
                    flex: 1,
                    padding: { xs: 2, sm: 3 },
                    pb: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', sm: 4 }
                }}>

                {/* Inline status banner — appears above all content when an
                    action just completed or failed. Replaces the old corner
                    toast notification. */}
                <Box sx={{ maxWidth: 900, mx: 'auto', mb: this.state.inlineStatus.message ? 1.5 : 0 }}>
                    <InlineStatus
                        message={this.state.inlineStatus.message}
                        variant={this.state.inlineStatus.variant}
                        onClose={this.clearStatus}
                    />
                </Box>

                {/* Map Shaper Tab Content */}
                {this.state.activeTab === 0 && (
                    <>
                        {/* Progress Stepper */}
                        <StepProgress currentStep={currentStep} steps={steps} />

                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    {/* Step 1a — photo upload + shape pick.
                        Upload on its own row so it's the visual anchor of step
                        1; category chips + shape select underneath share a
                        responsive row. */}
                    <GlassCard
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            mb: { xs: 1.5, sm: 2 }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ImageUpload
                                onSelected={this.onImageSelected}
                                fileId={this.props.photoId}
                            />
                        </Box>

                        <CategoryChips
                            value={this.state.selectedCategory}
                            onChange={(v) => this.onCategoryChange({ target: { value: v } })}
                            options={categoryOptions}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ width: { xs: '100%', sm: 360 } }}>
                                <SearchablePicker
                                    value={this.state.selectedShape}
                                    onChange={(v) => this.onShapeChange({ target: { value: v } })}
                                    options={shapeOptionsResult.options}
                                    groups={shapeOptionsResult.groups}
                                    label={this.state.selectedCategory ? 'Map Shape' : 'Pick a category first'}
                                    placeholder={
                                        this.state.selectedCategory
                                            ? 'Search shapes…'
                                            : 'Pick a category above first'
                                    }
                                    disabled={!this.state.selectedCategory}
                                />
                            </Box>
                        </Box>
                    </GlassCard>

                        {/* Step 2a — optional photo framing (move/zoom inside the shape) */}
                        <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                                mb: 1,
                                '& .MuiAccordionSummary-root': { minHeight: 'auto', py: 0.5, px: 0 },
                                '& .MuiAccordionDetails-root': { px: 0, pt: 1, pb: 0 }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
                            >
                                <OpenWith sx={{ color: PALETTE.primary }} />
                                <Typography sx={{ fontWeight: 600, color: PALETTE.text }}>
                                    Photo positioning
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: PALETTE.textMuted, display: { xs: 'none', sm: 'block' } }}
                                >
                                    (optional — move &amp; zoom inside the shape)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <PhotoFramingControl
                                    framing={this.state.framing}
                                    onChange={this.handleFramingChange}
                                    disabled={!this.state.selectedImageUri}
                                    slack={this.getFramingSlack()}
                                />
                            </AccordionDetails>
                        </Accordion>

                        {/* Step 2b — optional border styling, collapsed to keep the mobile flow short */}
                        <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                                mb: 1,
                                '& .MuiAccordionSummary-root': { minHeight: 'auto', py: 0.5, px: 0 },
                                '& .MuiAccordionDetails-root': { px: 0, pt: 1, pb: 0 }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
                            >
                                <Palette sx={{ color: PALETTE.primary }} />
                                <Typography sx={{ fontWeight: 600, color: PALETTE.text }}>
                                    Border options
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: PALETTE.textMuted, display: { xs: 'none', sm: 'block' } }}
                                >
                                    (optional — outline width &amp; color)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: 2
                                }}>
                                    <SelectDropdown
                                        value={this.state.selectedBorder}
                                        onChange={this.onBorderChange}
                                        options={borderSizeOptions}
                                        label="Border Size"
                                        minWidth={{ xs: 0, sm: '160px' }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Border color:</Typography>
                                        <ColorPickerControl
                                            color={this.state.borderColor}
                                            onChange={this.handleColorSliderChange}
                                            onPresetClick={this.handlePresetColorClick}
                                        />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Primary CTA — Shape Photo (or Re-shape after a result exists) */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <ActionButton
                                variant="primary"
                                startIcon={<AutoFixHigh />}
                                disabled={!this.state.selectedImageUri || !this.state.selectedShape}
                                loading={this.props.loading}
                                onClick={this.shapePhoto}
                                sx={{
                                    minWidth: { xs: '100%', sm: '280px' },
                                    height: '56px',
                                    fontSize: '1.05rem'
                                }}
                            >
                                {this.state.shapedPhoto ? 'Re-shape Photo' : 'Shape Photo'}
                            </ActionButton>
                        </Box>

                    {/* Single preview panel — evolves with state. Original photo is
                        hidden once a shaped result exists (the result IS the answer). */}
                    <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Off-screen canvas used by the shape pipeline */}
                            <Box sx={{ width: '0px', height: '0px', overflow: 'hidden' }}>
                                <canvas ref={this.canvasRef} />
                            </Box>

                            {(this.props.loading || this.state.loading) ? (
                                <ProcessingStatus
                                    progress={this.state.progress}
                                    label={this.state.loading
                                        ? 'Uploading your design…'
                                        : `Shaping into ${shaperShapeName}…`}
                                />
                            ) : this.state.shapedPhoto ? (
                                /* Result + export actions, hero treatment */
                                <>
                                    <Box sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', sm: 640 },
                                        mx: 'auto',
                                        minHeight: { xs: '280px', md: '400px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={this.state.shapedPhoto.data}
                                            alt="Shaped result"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '60vh',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 2 }}>
                                        <QuickOrderStrip
                                            defaultLabel={shaperShapeName}
                                            prepareImage={this.prepareShaperUpload}
                                            onOrder={this.handleShaperProductOrder}
                                            designSrc={this.state.shapedPhoto.data}
                                        />
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'row', sm: 'row' },
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                        gap: { xs: 0.75, sm: 1 },
                                        mt: 1
                                    }}>
                                        <ActionButton
                                            id="save-to-file-button"
                                            variant="ghost"
                                            startIcon={<Download sx={{ fontSize: 18 }} />}
                                            onClick={this.saveToFile}
                                            sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                                        >
                                            Download
                                        </ActionButton>
                                        <SaveMapButton
                                            getSnapshot={this.getShaperSnapshot}
                                            openMap={this.state.openMap}
                                            onSaved={(m) => this.setState({ openMap: m })}
                                            onStatus={this.showToast}
                                            variant="ghost"
                                            sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                                        />
                                        <ShareControl
                                            kind="shaper"
                                            shapeName={shaperShapeName}
                                            getBlob={this.getShareBlob}
                                            fileName={`map-shaped-${shaperFileSlug}.png`}
                                            trackId="map_shaper"
                                            variant="ghost"
                                            sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                                        />
                                    </Box>
                                </>
                            ) : this.state.selectedImageUri ? (
                                /* Original photo (only before shaping) + contextual hint */
                                <>
                                    <Box sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', sm: 640 },
                                        mx: 'auto',
                                        minHeight: { xs: '240px', md: '360px' },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={this.state.selectedImageUri}
                                            alt="Source for map shaping"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '50vh',
                                                objectFit: 'contain',
                                                borderRadius: 8
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ textAlign: 'center', color: PALETTE.textMuted, mt: 2 }}>
                                        {this.state.selectedShape
                                            ? '👆 Tap "Shape Photo" to crop into a map silhouette'
                                            : '👆 Choose a map shape above to continue'}
                                    </Typography>
                                </>
                            ) : (
                                /* Empty state — friendly illustration + nudge.
                                   Replaces the old icon-strip with a single
                                   warm illustrated scene matching the brand. */
                                <EmptyState
                                    image="/brand/empty-photo.png"
                                    alt=""
                                    title="Upload a photo to start"
                                    hint="Then pick a map shape to crop your photo into its silhouette."
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                    </>
                )}

                {/* Travel Map Tab Content */}
                {this.state.activeTab === 1 && (
                    <TravelSaveContext.Provider value={travelSaveValue}>
                        {/* Searchable picker — type "japan" to jump to Japan,
                            "uk" for UK counties, "parks" for National Parks. */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ width: { xs: '100%', sm: 360 } }}>
                                <SearchablePicker
                                    value={this.state.mapType}
                                    onChange={this.handleMapTypeChange}
                                    groups={MAP_TYPE_GROUPS}
                                    label="Travel Map"
                                    placeholder='Search regions… (try "japan")'
                                />
                            </Box>
                        </Box>

                        {this.state.mapType === 'usa' && <TravelMapSection />}
                        {this.state.mapType === 'parks' && <NationalParksMapSection />}
                        {this.state.mapType === 'canada' && <CanadaMapSection />}
                        {this.state.mapType === 'europe' && <EuropeMapSection />}
                        {this.state.mapType === 'eu' && <EUMapSection />}
                        {this.state.mapType === 'asia' && <AsiaMapSection />}
                        {this.state.mapType === 'africa' && <AfricaMapSection />}
                        {this.state.mapType === 'south-america' && <SouthAmericaMapSection />}
                        {this.state.mapType === 'latin-america' && <LatinAmericaMapSection />}
                        {this.state.mapType === 'world' && <WorldMapSection />}
                        {this.state.mapType === 'uk-counties' && <UKCountiesMapSection />}
                        {this.state.mapType === 'australia' && <AustraliaMapSection />}
                        {this.state.mapType === 'new-zealand' && <NewZealandMapSection />}
                        {this.state.mapType === 'mexico' && <MexicoMapSection />}
                        {this.state.mapType === 'germany' && <GermanyMapSection />}
                        {this.state.mapType === 'france' && <FranceMapSection />}
                        {this.state.mapType === 'italy' && <ItalyMapSection />}
                        {this.state.mapType === 'spain' && <SpainMapSection />}
                        {this.state.mapType === 'switzerland' && <SwitzerlandMapSection />}
                        {this.state.mapType === 'japan' && <JapanMapSection />}
                        {this.state.mapType === 'india' && <IndiaMapSection />}
                        {this.state.mapType === 'brazil' && <BrazilMapSection />}
                        {this.state.mapType === 'argentina' && <ArgentinaMapSection />}
                        {this.state.mapType === 'caribbean' && <CaribbeanMapSection />}
                    </TravelSaveContext.Provider>
                )}

                </Box>

                <Footer />
            </Box>
        );
    }
}

Main.propTypes = {
    photoId: PropTypes.string.isRequired,
    shapedPhoto: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    searchParams: PropTypes.object,
    setSearchParams: PropTypes.func,
    authReady: PropTypes.bool,
    isSignedIn: PropTypes.bool,
    tool: PropTypes.oneOf(['travel', 'shaper'])
};

function mapStateToProps(state) {
    return {
        photoId: state.data.photoId,
        shapedPhoto: state.data.shapedPhoto,
        loading: state.ajaxCallsInProgress > 0
    };
}

function mapDispatchToProps(dispatch) {
    return {
        shapeActions: bindActionCreators(shapeActions, dispatch),
        orderProduct: bindActionCreators(orderProduct, dispatch),
    };
}

// Wrapper to provide URL search params + auth state to the class component.
function MainWithRouter(props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { authReady, isSignedIn } = useAuth();
    return (
        <Main
            {...props}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            authReady={authReady}
            isSignedIn={isSignedIn}
        />
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MainWithRouter);